"use client";

import { Canvas, useFrame } from "@react-three/fiber";
import { Environment, useGLTF } from "@react-three/drei";
import { Suspense, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import * as THREE from "three";
import gsap from "gsap";
import ScrollTrigger from "gsap/dist/ScrollTrigger";
import Hero from "../MainHero/Hero";
import styles from "./Motto.module.css";

gsap.registerPlugin(ScrollTrigger);

const clamp01 = (value) => Math.min(1, Math.max(0, value));
const smoothstep = (value) => {
  const progress = clamp01(value);
  return progress * progress * (3 - 2 * progress);
};

function CapsuleModel({ baseScale, progressRef }) {
  const { scene, materials, nodes } = useGLTF("/model/scene.gltf");
  const model = useMemo(() => scene.clone(true), [scene]);
  const modelRef = useRef(null);
  const upperPartRef = useRef(null);
  const lowerPartRef = useRef(null);
  const originalTransforms = useRef(null);

  useEffect(() => {
    upperPartRef.current = model.getObjectByName(nodes.Roundcube001_Material001_0.name);
    lowerPartRef.current = model.getObjectByName(nodes.Roundcube_Material_0.name);

    if (upperPartRef.current && lowerPartRef.current) {
      originalTransforms.current = {
        upperPosition: upperPartRef.current.position.clone(),
        upperRotation: upperPartRef.current.rotation.clone(),
        lowerPosition: lowerPartRef.current.position.clone(),
        lowerRotation: lowerPartRef.current.rotation.clone(),
      };
    }
  }, [model, nodes]);

  useEffect(() => {
    Object.values(materials).forEach((material) => {
      if (material.isMeshStandardMaterial || material.isMeshPhysicalMaterial) {
        material.metalness = 0;
        material.roughness = 0.5;
        material.envMapIntensity = 1;
        material.emissiveIntensity = 0.05;
        material.needsUpdate = true;
      }
    });
  }, [materials]);

  useFrame((state) => {
    const group = modelRef.current;
    const upper = upperPartRef.current;
    const lower = lowerPartRef.current;
    const originals = originalTransforms.current;
    if (!group || !upper || !lower || !originals) return;

    const progress = progressRef.current;
    const split = smoothstep((progress - 0.08) / 0.58);
    const separation = split * 1.42;
    const shrink = THREE.MathUtils.lerp(1, 0.58, smoothstep(progress / 0.82));
    const autoRotation = state.clock.elapsedTime * 0.24;

    upper.position.copy(originals.upperPosition);
    upper.position.y += separation;
    upper.rotation.copy(originals.upperRotation);

    lower.position.copy(originals.lowerPosition);
    lower.position.y -= separation;
    lower.rotation.copy(originals.lowerRotation);

    // Rotation never rewinds with reverse scroll. Only the distance between the
    // two halves is scroll-controlled, so they reunite in the current pose.
    group.rotation.x = autoRotation;
    group.rotation.z = autoRotation;
    group.scale.set(
      baseScale[0] * shrink,
      baseScale[1] * shrink,
      baseScale[2] * shrink,
    );

    // Keep rendering while the capsule is visible, then pause once it has faded.
    if (progress < 0.78) state.invalidate();
  });

  return <primitive ref={modelRef} object={model} />;
}

useGLTF.preload("/model/scene.gltf");

export default function Motto() {
  const sectionRef = useRef(null);
  const model3DRef = useRef(null);
  const capsuleProgressRef = useRef(0);
  const invalidateRef = useRef(null);
  const [screenWidth, setScreenWidth] = useState(1440);

  useEffect(() => {
    const updateWidth = () => setScreenWidth(window.innerWidth);
    updateWidth();
    window.addEventListener("resize", updateWidth, { passive: true });
    return () => window.removeEventListener("resize", updateWidth);
  }, []);

  const updateCapsule = useCallback((progress) => {
    capsuleProgressRef.current = progress;
    invalidateRef.current?.();

    if (model3DRef.current) {
      const fade = 1 - smoothstep((progress - 0.56) / 0.22);
      model3DRef.current.style.setProperty("--capsule-opacity", String(0.7 * fade));
      model3DRef.current.style.visibility = fade < 0.01 ? "hidden" : "visible";
      model3DRef.current.style.zIndex = progress > 0.08 ? "2" : "5";
    }
  }, []);

  useLayoutEffect(() => {
    const section = sectionRef.current;
    if (!section) return undefined;

    const state = { progress: 0 };
    const context = gsap.context(() => {
      const tween = gsap.to(state, {
        progress: 1,
        ease: "none",
        onUpdate: () => updateCapsule(state.progress),
        scrollTrigger: {
          trigger: section,
          start: "top top",
          end: "bottom top",
          scrub: 0.24,
          invalidateOnRefresh: true,
          fastScrollEnd: true,
          onRefresh: (self) => updateCapsule(self.progress),
        },
      });

      return () => tween.kill();
    }, section);

    return () => {
      context.revert();
      updateCapsule(0);
    };
  }, [updateCapsule]);

  const settings = useMemo(() => {
    if (screenWidth <= 480) {
      return { scale: [0.5, 0.5, 0.5], camera: [0, 0, 8], fov: 60 };
    }
    if (screenWidth <= 768) {
      return { scale: [0.7, 0.7, 0.7], camera: [0, 0, 6], fov: 55 };
    }
    if (screenWidth <= 1024) {
      return { scale: [0.9, 0.9, 0.9], camera: [0, 0, 5], fov: 50 };
    }
    return { scale: [1, 1, 1], camera: [0, 0, 5], fov: 50 };
  }, [screenWidth]);

  return (
    <div>
      <div ref={sectionRef} className={`Main_motto ${styles.mainMotto}`}>
        <div className={styles.textContainer} style={{ zIndex: 1 }}>
          <p className={`mottoText ${styles.mottoText}`} style={{ fontSize: "1rem" }}>Logvplus</p>
        </div>
        <div className={styles.textContainer}>
          <h1 className={`mottoText ${styles.mottoText}`}>Experience the</h1>
        </div>
        <div className={styles.textContainer}>
          <h1 className={`mottoText ${styles.mottoText}`}>Essence of Endurance.</h1>
        </div>

        <div ref={model3DRef} className={`model3D ${styles.model3D}`}>
          <Canvas
            frameloop="demand"
            dpr={[1, 1.4]}
            camera={{ position: settings.camera, fov: settings.fov }}
            gl={{ antialias: true, alpha: true, powerPreference: "high-performance" }}
            onCreated={({ invalidate }) => {
              invalidateRef.current = invalidate;
              invalidate();
            }}
          >
            <ambientLight intensity={0.4} />
            <directionalLight position={[10, 10, 5]} intensity={1.2} />
            <pointLight position={[-10, -10, -5]} intensity={0.5} />
            <pointLight position={[5, -5, 5]} intensity={0.3} color="#ffffff" />
            <Environment preset="city" />
            <Suspense fallback={null}>
              <CapsuleModel baseScale={settings.scale} progressRef={capsuleProgressRef} />
            </Suspense>
          </Canvas>
        </div>
      </div>

      <Hero />
    </div>
  );
}
